import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  X, ChevronRight, ChevronLeft, CheckCircle, 
  LayoutDashboard, Package, Boxes, Truck, MapPin, 
  ClipboardList, Factory, Tag, History, Bot, GraduationCap,
  SkipForward
} from 'lucide-react';
import { toast } from 'sonner';

const buildTutorialContent = (appName) => ({
  welcome: {
    title: `Welcome to ${appName}!`,
    description: "Let's take a quick tour of the system. This tutorial will guide you through all the key features.",
    icon: GraduationCap,
    path: "/",
    tips: [
      `${appName} helps you manage inventory, BOMs, and production`,
      "All inventory movements are traceable and auditable",
      "Use QR codes to quickly access any record"
    ]
  },
  dashboard: {
    title: "Dashboard Overview",
    description: "Your command center showing key metrics, alerts, and quick actions.",
    icon: LayoutDashboard,
    path: "/",
    tips: [
      "View total counts of items, vendors, BOMs, and work orders",
      "See low stock alerts that need attention",
      "Use quick actions to jump to common tasks"
    ]
  },
  "product-types": {
    title: "Product Types",
    description: "Define your finished product families and models here.",
    icon: Package,
    path: "/product-types",
    tips: [
      "Product Types represent what you manufacture",
      "Each product type can have multiple BOM variants",
      "Link default BOM variants for faster work order creation"
    ]
  },
  items: {
    title: "Item Master",
    description: "Manage all components, materials, packaging, and consumables.",
    icon: Boxes,
    path: "/items",
    tips: [
      "Each item has a unique SKU for tracking",
      "Set reorder points to get low stock alerts",
      "Choose tracking method: None, Lot, or Serial"
    ]
  },
  vendors: {
    title: "Vendor Management",
    description: "Track your suppliers and their lead times.",
    icon: Truck,
    path: "/vendors",
    tips: [
      "Add contact info and payment terms",
      "Track lead times for planning",
      "Link vendors to items for preferred sourcing"
    ]
  },
  locations: {
    title: "Locations & Bins",
    description: "Define your warehouse storage structure.",
    icon: MapPin,
    path: "/locations",
    tips: [
      "Create locations for different warehouse areas",
      "Use bin types: Receiving, Storage, WIP, Finished Goods",
      "Print location labels with QR codes"
    ]
  },
  boms: {
    title: "Bill of Materials",
    description: "Create and manage BOMs with components and revisions.",
    icon: ClipboardList,
    path: "/boms",
    tips: [
      "BOMs define what components make up a product",
      "Use variants for different product configurations",
      "Release BOMs before using in production"
    ]
  },
  "work-orders": {
    title: "Work Orders",
    description: "Plan and track production with time tracking.",
    icon: Factory,
    path: "/work-orders",
    tips: [
      "Work orders reference released BOMs",
      "Clock in/out to track time spent",
      "Track progress as units are completed"
    ]
  },
  "finished-units": {
    title: "Finished Units",
    description: "Track serialized products with full traceability.",
    icon: Tag,
    path: "/finished-units",
    tips: [
      "Each finished unit gets a unique serial number",
      "View QR codes for easy scanning",
      "Track status: In Stock, Shipped, etc."
    ]
  },
  transactions: {
    title: "Inventory Transactions",
    description: "Complete history of all inventory movements.",
    icon: History,
    path: "/transactions",
    tips: [
      "Record receives, transfers, and consumes",
      "All transactions are logged with user and timestamp",
      "View transaction history by item or type"
    ]
  },
  labels: {
    title: "Labels & QR Codes",
    description: "Create label templates and print labels.",
    icon: Tag,
    path: "/labels",
    tips: [
      "Create custom label templates",
      "Configure fields and sizes",
      "Print labels with QR codes for any entity"
    ]
  },
  "ai-assistant": {
    title: "AI Assistant",
    description: "Get AI-powered insights and analysis.",
    icon: Bot,
    path: "/ai-assistant",
    tips: [
      "Ask questions about inventory health",
      "Get supply chain recommendations",
      "Run system checks for vulnerabilities"
    ]
  },
  complete: {
    title: "You're All Set!",
    description: "You've completed the tutorial. You can restart it anytime from your profile.",
    icon: CheckCircle,
    path: "/",
    tips: [
      "Start by creating your first Product Type",
      "Add items to build your inventory",
      "Create a BOM and start production!"
    ]
  }
});

const TutorialOverlay = () => {
  const { api, user } = useAuth();
  const { branding } = useBranding();
  const appName = branding?.app_name || 'BOM Maestro';
  const tutorialContent = React.useMemo(() => buildTutorialContent(appName), [appName]);
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState(null);

  useEffect(() => {
    if (user) {
      checkTutorialProgress();
    }
  }, [user]);

  const checkTutorialProgress = async () => {
    try {
      const [progressRes, sectionsRes] = await Promise.all([
        api().get('/tutorial/progress'),
        api().get('/tutorial/sections')
      ]);
      setProgress(progressRes.data);
      setSections(sectionsRes.data);
      
      // Show tutorial if not completed or skipped
      if (!progressRes.data.completed_at && !progressRes.data.skipped) {
        setCurrentSection(progressRes.data.current_section || 'welcome');
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Failed to check tutorial progress');
    }
  };

  const handleNext = async () => {
    if (!currentSection) return;
    
    try {
      const res = await api().put(`/tutorial/progress?section_id=${currentSection}`);
      setProgress(prev => ({
        ...prev,
        completed_sections: res.data.completed_sections,
        current_section: res.data.next_section
      }));
      
      const content = tutorialContent[res.data.next_section];
      if (content?.path && content.path !== location.pathname) {
        navigate(content.path);
      }
      
      setCurrentSection(res.data.next_section);
      
      if (res.data.next_section === 'complete') {
        toast.success('Tutorial completed!');
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handlePrev = () => {
    const currentIdx = sections.findIndex(s => s.id === currentSection);
    if (currentIdx > 0) {
      const prevSection = sections[currentIdx - 1].id;
      const content = tutorialContent[prevSection];
      if (content?.path && content.path !== location.pathname) {
        navigate(content.path);
      }
      setCurrentSection(prevSection);
    }
  };

  const handleSkip = async () => {
    try {
      await api().post('/tutorial/skip');
      setIsOpen(false);
      toast.info('Tutorial skipped. You can restart it from settings.');
    } catch (error) {
      toast.error('Failed to skip tutorial');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const restartTutorial = async () => {
    try {
      await api().post('/tutorial/reset');
      setCurrentSection('welcome');
      setProgress({ completed_sections: [], current_section: 'welcome' });
      setIsOpen(true);
      navigate('/');
    } catch (error) {
      toast.error('Failed to restart tutorial');
    }
  };

  if (!isOpen || !currentSection) {
    return null;
  }

  const content = tutorialContent[currentSection];
  if (!content) return null;

  const currentIdx = sections.findIndex(s => s.id === currentSection);
  const progressPercent = ((progress?.completed_sections?.length || 0) / (sections.length - 1)) * 100;
  const IconComponent = content.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-lg mx-4 shadow-2xl animate-slideIn">
        <CardContent className="p-0">
          {/* Header */}
          <div className="bg-slate-900 text-white p-4 rounded-t-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-orange-400" />
                <span className="font-semibold">Tutorial</span>
                <span className="text-slate-400 text-sm">({currentIdx + 1}/{sections.length})</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleClose} className="text-white hover:bg-slate-800">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <Progress value={progressPercent} className="h-1 bg-slate-700" />
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-orange-100 rounded-sm">
                <IconComponent className="w-8 h-8 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{content.title}</h2>
                <p className="text-slate-500">{content.description}</p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-sm p-4 mb-6">
              <p className="text-sm font-semibold text-slate-700 mb-2">Tips:</p>
              <ul className="space-y-2">
                {content.tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-slate-500"
              >
                <SkipForward className="w-4 h-4 mr-2" />
                Skip Tutorial
              </Button>
              
              <div className="flex gap-2">
                {currentIdx > 0 && (
                  <Button variant="outline" onClick={handlePrev} className="rounded-sm">
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                )}
                <Button 
                  onClick={handleNext} 
                  className="bg-orange-500 hover:bg-orange-600 rounded-sm"
                >
                  {currentSection === 'complete' ? 'Finish' : 'Next'}
                  {currentSection !== 'complete' && <ChevronRight className="w-4 h-4 ml-1" />}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Export both the overlay and a button to restart
export const RestartTutorialButton = () => {
  const { api } = useAuth();
  const navigate = useNavigate();

  const handleRestart = async () => {
    try {
      await api().post('/tutorial/reset');
      navigate('/');
      window.location.reload(); // Refresh to show tutorial
    } catch (error) {
      toast.error('Failed to restart tutorial');
    }
  };

  return (
    <Button variant="outline" onClick={handleRestart} className="rounded-sm">
      <GraduationCap className="w-4 h-4 mr-2" />
      Restart Tutorial
    </Button>
  );
};

export default TutorialOverlay;
